<template>
  <v-container fluid class="pa-0 ma-0">
    <v-img
      :src="imageFromChallengeUUID"
      gradient="to bottom, rgba(0,0,0,0), rgba(0,0,0,0.9)"
      height="200"
    />
    <v-row
      justify="center"
      v-if="challengeLoaded"
      style="position: relative; top: -120px;"
    >
      <v-stepper
        style="width: 75%;"
        v-model="step"
        vertical
      >
        <v-stepper-step
          color="rgb(255, 149, 24)"
          :complete="step > 1"
          step="1"
          editable
        >
          Pick a name and organization for your challenge
        </v-stepper-step>
        <v-stepper-content step="1">
          <v-autocomplete
            v-model="selectedOrg"
            :items="filteredOrganizations"
            :disabled="!isNewChallenge"
            item-text="name"
            item-value="uuid"
            color="orange"
            label="Organization"
            return-object
          >
          </v-autocomplete>
          <v-text-field
            v-model="currentlyViewedChallenge.name"
            label="Challenge Name"
            color="orange"
            persistent-hint
            :maxlength="255"
            required
            autofocus
            :rules="[rules.required]"
          />
          <v-btn
            color="rgb(255, 149, 24)"
            class="nextBtn"
            @click="step = 2"
          >
            Continue
          </v-btn>
        </v-stepper-content>

        <v-stepper-step
            color="rgb(255, 149, 24)"
            :complete="step > 2"
            step="2"
            editable
          >
          Change the phrase and colours
        </v-stepper-step>
        <v-stepper-content step="2">
          <ChallengeHeader
            @addNodeColor="addNodeColorDialog = true"
            @addFontColor="addFontColorDialog = true"
          />
          <v-btn
            color="rgb(255, 149, 24)"
            class="nextBtn"
            @click="step = 3"
          >
            Continue
          </v-btn>
        </v-stepper-content>

        <v-stepper-step
            color="rgb(255, 149, 24)"
            :complete="step > 3"
            step="3"
            editable
          >
          Pick dates for your challenge
        </v-stepper-step>
        <v-stepper-content step="3">
          <DatesCard />
          <v-btn
            color="rgb(255, 149, 24)"
            class="nextBtn"
            @click="step = 4"
          >
            Continue
          </v-btn>
        </v-stepper-content>

        <v-stepper-step
            color="rgb(255, 149, 24)"
            :complete="step > 4"
            step="4"
            editable
          >
          Pick Participants and facilitators
        </v-stepper-step>
        <v-stepper-content step="4">
          <ParticipantsCard
            @invite-response="showInviteResponseSnackbar"
            :organizationUUID="selectedOrg ? selectedOrg.uuid : ''"
          />
          <v-btn
            color="rgb(255, 149, 24)"
            class="nextBtn"
            @click="step = 5"
          >
            Continue
          </v-btn>
        </v-stepper-content>

        <v-stepper-step
            color="rgb(255, 149, 24)"
            :complete="step > 5"
            step="5"
            editable
          >
          Save and go back to menu
        </v-stepper-step>
        <v-stepper-content step="5">
          <v-btn
            color="rgb(255, 149, 24)"
            dark
            bottom
            right
            x-large
            link
            @click="saveAndGoToMenu"
            @close="snackbar = false"
            width="50%"
            class="save-changes-button"
          >
            <div>
              {{
                currentlyViewedChallenge.archived
                ? 'Go back to menu'
                : 'Save changes and go back to menu'
              }}
            </div>
          </v-btn>
        </v-stepper-content>
      </v-stepper>
    </v-row>
    <v-btn
      color="rgb(255, 149, 24)"
      dark
      fixed
      bottom
      right
      x-large
      link
      style="bottom: 1em;"
      @click="saveAndGoToChallenge"
        >
         {{ goToChallengeText }}
      </v-btn>
    <v-row class="snackbar-container" justify="center">
      <BaseSnackbar
        :visible="snackbarVisible"
        :text="snackbarText"
        @close="snackbarVisible = false"
        class="snackbar"
      />
    </v-row>
  <ValidationModal
    :visible.sync="validationModalVisible"
  />
  </v-container>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'

import ChallengeHeader from './challengeHeader/ChallengeHeader'
import ParticipantsCard from './participantsCard/ParticipantsCard'
import DatesCard from './datesCard/DatesCard'
import BaseSnackbar from '@/components/base/BaseSnackbar'
import ValidationModal from '@/components/challenge/ValidationModal'

export default {
  name: 'ChallengePage',

  components: {
    ChallengeHeader,
    ParticipantsCard,
    DatesCard,
    BaseSnackbar,
    ValidationModal
  },

  data: () => ({
    challengeLoaded: false,
    snackbarVisible: false,
    snackbarText: '',
    snackbarDefaultTimeout: 5000,
    step: 1,
    selectedOrg: null,
    validationModalVisible: false,
    rules: {
      required: value => (value || '').length > 0 || 'This field is required'
    }
  }),

  async created () {
    if (this.isNewChallenge) {
      // const createChallengeResponse = await this.requestCreateChallenge()
      // if (createChallengeResponse.success !== false) {
      //   this.$router.push(`/admin-dashboard/challenges/${createChallengeResponse.challenge.uuid}`)
      // }
      this.setCurrentlyViewedChallenge(this.newChallenge)

      this.selectedOrg = this.filteredOrganizations.find(o => o.uuid === this.currentlyViewedChallenge.organization)
    } else {
      const challengeResponse = await this.requestCurrentlyViewedChallenge(this.$route.params.id)

      if (challengeResponse.success === false) {
        this.$router.push('/admin-dashboard/active-challenges')
      }

      this.selectedOrg = await this.requestOrganization(this.currentlyViewedChallenge.organization)
      console.log(this.selectedOrg)
    }

    this.challengeLoaded = true
  },

  async beforeDestroy () {
    this.setCurrentlyViewedChallenge({})
  },

  computed: {
    ...mapState({
      currentlyViewedChallenge: state => state.challenge.currentlyViewedChallenge,
      organizations: state => state.organization.organizations,
      userInfo: state => state.auth.userInfo
    }),

    imageFromChallengeUUID () {
      // this method is a computed in case currentlyViewedChallenge
      // is not populated on mount
      if (this.currentlyViewedChallenge && Object.keys(this.currentlyViewedChallenge).length !== 0) {
        const uuidLastDigit = this.currentlyViewedChallenge.uuid ? this.currentlyViewedChallenge.uuid[this.currentlyViewedChallenge.uuid.length - 1] : 0
        const uuidLastDigitString = parseInt('0x' + uuidLastDigit).toString()

        const imageNumber = uuidLastDigitString.padStart(2, '0')

        return require('@/assets/example_challenge_images/' + imageNumber + '.jpg')
      }
      return ''
    },

    goToChallengeText () {
      if (this.$route.name === 'new-challenge') {
        return 'Create and go to challenge'
      } else if (this.$route.name === 'challenge-page') {
        return 'Save and go to challenge'
      } else {
        throw new Error(`Route name ${this.$route.name} is invalid`)
      }
    },

    isNewChallenge () {
      if (this.$route.name === 'new-challenge') {
        return true
      } else if (this.$route.name === 'challenge-page') {
        return false
      } else {
        throw new Error(`Route name ${this.$route.name} is invalid`)
      }
    },

    filteredOrganizations () {
      if (this.userInfo.account.systemAdmin) {
        return this.organizations
      } else {
        let orgsAllowed = this.userInfo.organizations.filter(o => o.scope !== 'org_standard')
        orgsAllowed = orgsAllowed.map(o => {
          const orgsWithName = Object.assign({}, o)
          const orgName = this.organizations.find(org => org.uuid === o.organization)
          if (orgName !== undefined) {
            orgsWithName.name = orgName.name
            orgsWithName.uuid = orgsWithName.organization
          } else {
            throw new Error(`Organization ${JSON.stringify(o)} not found in global organization list`)
          }
          return orgsWithName
        })

        return orgsAllowed
      }
    },

    newChallenge () {
      return {
        organization: this.organizations[0].uuid,
        name: '',
        phrase: 'How might',
        nodeColors: [
          {
            label: 'Red',
            code: '#ff0000'
          },
          {
            label: 'Orange',
            code: '#ffa500'
          },
          {
            label: 'Yellow',
            code: '#ffff00'
          },
          {
            label: 'Green',
            code: '#008000'
          },
          {
            label: 'Blue',
            code: '#0000ff'
          },
          {
            label: 'Indigo',
            code: '#4b0082'
          },
          {
            label: 'Violet',
            code: '#ee82ee'
          },
          {
            label: 'Black',
            code: '#000000'
          },
          {
            label: 'White',
            code: '#ffffff'
          }
        ],
        defaultNodeColor: '#ffa500',
        fontColors: [
          {
            label: 'Red',
            code: '#ff0000'
          },
          {
            label: 'Orange',
            code: '#ffa500'
          },
          {
            label: 'Yellow',
            code: '#ffff00'
          },
          {
            label: 'Green',
            code: '#008000'
          },
          {
            label: 'Blue',
            code: '#0000ff'
          },
          {
            label: 'Indigo',
            code: '#4b0082'
          },
          {
            label: 'Violet',
            code: '#ee82ee'
          },
          {
            label: 'Black',
            code: '#000000'
          },
          {
            label: 'White',
            code: '#ffffff'
          }
        ],
        defaultFontColor: '#000000',
        linkColors: [
          {
            label: 'Red',
            code: '#ff0000'
          },
          {
            label: 'Orange',
            code: '#ffa500'
          },
          {
            label: 'Yellow',
            code: '#ffff00'
          },
          {
            label: 'Green',
            code: '#008000'
          },
          {
            label: 'Blue',
            code: '#0000ff'
          },
          {
            label: 'Indigo',
            code: '#4b0082'
          },
          {
            label: 'Violet',
            code: '#ee82ee'
          },
          {
            label: 'Black',
            code: '#000000'
          },
          {
            label: 'White',
            code: '#ffffff'
          }
        ],
        defaultLinkColor: '#000000',
        dotColors: [
          {
            label: 'Red',
            code: '#ff0000'
          },
          {
            label: 'Orange',
            code: '#ffa500'
          },
          {
            label: 'Yellow',
            code: '#ffff00'
          },
          {
            label: 'Green',
            code: '#008000'
          },
          {
            label: 'Blue',
            code: '#0000ff'
          },
          {
            label: 'Indigo',
            code: '#4b0082'
          },
          {
            label: 'Violet',
            code: '#ee82ee'
          },
          {
            label: 'Black',
            code: '#000000'
          },
          {
            label: 'White',
            code: '#ffffff'
          }
        ],
        defaultDotColor: '#000000',
        schedule: [],
        participants: [],
        facilitators: [this.userInfo.account.uuid]
      }
    }
  },
  watch: {
    selectedOrg: {
      deep: true,
      handler: async function () {
        if (this.isNewChallenge) {
          // const createChallengeResponse = await this.requestCreateChallenge()
          // if (createChallengeResponse.success !== false) {
          //   this.$router.push(`/admin-dashboard/challenges/${createChallengeResponse.challenge.uuid}`)
          // }
          const newChallengeCopy = JSON.parse(JSON.stringify(this.newChallenge))
          newChallengeCopy.organization = this.selectedOrg.uuid
          newChallengeCopy.name = this.currentlyViewedChallenge.name

          this.setCurrentlyViewedChallenge(newChallengeCopy)

          this.selectedOrg = this.filteredOrganizations.find(o => {
            return o.uuid === this.currentlyViewedChallenge.organization
          })
        }
      }
    }
  },
  methods: {
    ...mapActions([
      'requestCurrentlyViewedChallenge',
      'requestCreateChallenge',
      'requestUpdateChallenge',
      'requestActivateChallenge',
      'requestAllOrganizations',
      'requestOrganization'
    ]),
    ...mapMutations(['setCurrentlyViewedChallenge']),

    async saveChanges () {
      // todo set the challenge organization to the selectedOrg.uuid
      // await this.requestUpdateChallenge(this.currentlyViewedChallenge)
      // this.$router.push(`/${this.userInfo.type}-dashboard`)

      console.log(this.currentlyViewedChallenge)

      if (this.currentlyViewedChallenge.archived) {
        return true
      }

      if (this.currentlyViewedChallenge.schedule.length === 0) {
        this.validationModalVisible = true
        return false
      }

      if (this.isNewChallenge) {
        const createChallengeResponse = await this.requestCreateChallenge({
          organization: this.selectedOrg.uuid,
          name: this.currentlyViewedChallenge.name,
          phrase: this.currentlyViewedChallenge.phrase,
          schedule: this.currentlyViewedChallenge.schedule,
          participants: this.currentlyViewedChallenge.participants,
          facilitators: this.currentlyViewedChallenge.facilitators
        })
        if (createChallengeResponse.success !== false) {
          this.currentlyViewedChallenge.uuid = createChallengeResponse.challenge.uuid
          this.currentlyViewedChallenge.organization = this.selectedOrg.uuid
        } else {
          this.showSnackbar('Could not create challenge')
          return false
        }
      }
      const currentlyViewedChallengeCopy = JSON.parse(JSON.stringify(this.currentlyViewedChallenge))
      // delete currentlyViewedChallengeCopy.participants
      // delete currentlyViewedChallengeCopy.facilitators
      await this.requestUpdateChallenge(currentlyViewedChallengeCopy)
      this.showSnackbar('Saved')
      return true
    },

    async saveAndGoToMenu () {
      if (await this.saveChanges()) {
        this.$router.push('/admin-dashboard/active-challenges')
      }
    },

    async saveAndGoToChallenge () {
      if (await this.saveChanges()) {
        this.$router.push(`/whiteboard/${this.currentlyViewedChallenge.uuid}`)
      }
    },

    showInviteResponseSnackbar (response) {
      if (response.success) {
        this.showSnackbar('An email will be sent once you save changes.')
      } else {
        this.showSnackbar(`Could not send email. ${response.message}`, 7000)
      }
    },

    showSnackbar (text, timeout = null) {
      let snackbarTimeout = this.snackbarDefaultTimeout
      if (timeout !== null) {
        snackbarTimeout = timeout
      }

      this.snackbarText = text
      this.snackbarVisible = true
      setTimeout(() => { this.snackbarVisible = false }, snackbarTimeout)
    }
  }
}
</script>

<style lang="sass" scoped>
.save-changes-container, .snackbar-container
  position: relative
  width: 100%

  .save-changes-button
    position: fixed
    bottom: 25px
    z-index: 200

.nextBtn
  margin-top: 45px

</style>

<style lang="sass">
.snackbar-container
  position: relative
  width: 100%

  .snackbar
    position: fixed !important
    margin-top: -80px

.v-list-item__mask
  background: rgba(255,149,24,0.4) !important
  color: black !important
</style>
